import React, { memo, useEffect, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';

import { authState, useGetLesson, useGoToErrorPage, useLessonTabs } from 'core';
import { isLessonToday, isLessonTomorrow } from 'services/LessonTimeService';
import { JournalPreloader } from 'atoms';
import { UserRole } from 'context';
import { LessonSkill } from 'components/types/lesson';
import { LessonComponent } from './Lesson';

interface ILessonProps {
  groupId?: string;
  currentLessonId: string;
  /** Навыки занятия — со страницы курса (ТЗ 0040, доводка). */
  lessonSkills: LessonSkill[];
  skillsGranted: boolean;
  hasLessonsToFinish: boolean;
  isFree: boolean;
  courseName: string;
  lessonsCount: number;
  /** Группа «без расписания» (считается на странице курса, только студенту). */
  hideSchedule?: boolean;
}

export const Lesson = memo(
  ({
    groupId,
    currentLessonId,
    lessonSkills,
    skillsGranted,
    hasLessonsToFinish,
    isFree,
    courseName,
    lessonsCount,
    hideSchedule = false,
  }: ILessonProps) => {
    const { active_role } = useReactiveVar(authState);
    const [userRole] = useState(active_role);

    const goToErrorPage = useGoToErrorPage();

    const { currentLesson, loading, error } = useGetLesson(currentLessonId);

    // ТЗ 0100: «сегодня/завтра» — по моменту начала в местном календаре
    // студента; без момента — по дате занятия.
    const dateIsNow = useMemo(
      () => (currentLesson ? isLessonToday(currentLesson) : undefined),
      [currentLesson],
    );

    const dateIsTomorrow = useMemo(
      () => (currentLesson ? isLessonTomorrow(currentLesson) : undefined),
      [currentLesson],
    );

    const { pathToDescription, pathToTasks, tabs } = useLessonTabs({
      groupId,
      lessonId: currentLessonId,
      userRole,
      currentLesson,
      dateIsNow,
      dateIsTomorrow,
      courseName,
      lessonsCount,
      hideSchedule,
    });

    useEffect(() => {
      if (error) {
        console.log({ lessonError: error });
        goToErrorPage();
      }
    }, [error, goToErrorPage]);

    return loading ? (
      <JournalPreloader />
    ) : (
      <LessonComponent
        currentLesson={currentLesson}
        lessonSkills={lessonSkills}
        skillsGranted={skillsGranted}
        tabs={tabs}
        pathToDescription={pathToDescription}
        pathToTasks={pathToTasks}
        hasLessonsToFinish={hasLessonsToFinish}
        activeRole={active_role as UserRole}
        isFree={isFree}
        courseName={courseName}
        hideSchedule={hideSchedule}
      />
    );
  },
);
